import { useState } from "react";
import { Loader2, MapPin, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

export const NewPortDialog = ({ cid, open, onOpenChange, onCreated }) => {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/customers/${cid}/anlop/ports`, { name: name.trim() });
      toast.success(`Havnen «${name.trim()}» er opprettet`);
      setName("");
      onOpenChange(false);
      onCreated(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="anlop-new-port-dialog">
        <DialogHeader>
          <DialogTitle>Ny havn</DialogTitle>
          <DialogDescription>Havnen får sin egen anløpsliste, Google-lenke og egne embed-koder. Innstillingene kopieres fra den første havnen, og du kan endre dem etterpå.</DialogDescription>
        </DialogHeader>
        <label className="space-y-1.5 block">
          <span className="text-sm font-medium text-slate-700">Navn på havnen</span>
          <Input value={name} maxLength={60} autoFocus onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && name.trim() && create()}
            placeholder="F.eks. Ålesund, Geiranger, Hellesylt" data-testid="anlop-new-port-name" />
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="anlop-new-port-cancel">Avbryt</Button>
          <Button onClick={create} disabled={busy || !name.trim()} data-testid="anlop-new-port-create">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Opprett havn
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const PortTabs = ({ cid, ports, pid, setPid, onPorts }) => {
  const [open, setOpen] = useState(false);
  const full = !ports.can_add;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2 flex flex-wrap items-center gap-2" data-testid="anlop-port-tabs" role="tablist" aria-label="Velg havn">
      {ports.ports.map((p) => (
        <button key={p.id} type="button" role="tab" aria-selected={pid === p.id} onClick={() => setPid(p.id)} data-testid={`anlop-port-tab-${p.id}`}
          className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm transition-colors ${pid === p.id ? "bg-[#0A192F] text-white font-medium" : "text-slate-700 hover:bg-slate-100"}`}>
          <MapPin className="w-3.5 h-3.5" />{p.name}
        </button>
      ))}
      <div className="ml-auto flex items-center gap-2">
        {full && ports.limit === 1 && <span className="text-xs text-slate-500" data-testid="anlop-port-locked">Flere havner krever utvidet pakke – kontakt oss</span>}
        {!(full && ports.limit === 1) && <span className="text-xs text-slate-500" data-testid="anlop-port-count">{ports.ports.length}{ports.limit ? ` av ${ports.limit}` : ""} havner</span>}
        <Button size="sm" variant="outline" disabled={full} onClick={() => setOpen(true)} title={full ? "Pakken deres har ikke plass til flere havner. Kontakt oss for å utvide." : undefined} data-testid="anlop-add-port">
          <Plus className="w-4 h-4 mr-1" />Ny havn
        </Button>
      </div>
      <NewPortDialog cid={cid} open={open} onOpenChange={setOpen} onCreated={(d) => { onPorts(d); setPid(d.created); }} />
    </div>
  );
};
